import type { Config } from 'tailwindcss';
import type { PluginAPI } from 'tailwindcss/types/config';

import tailwindTypographyPlugin from '@tailwindcss/typography';
import defaultTheme from 'tailwindcss/defaultTheme';
import tailwindTypographyConfig from './tailwindTypography.config';

export default {
  content: ['./src/**/*.{html,js,svelte,ts}'],
  theme: {
    colors: {
      black: '#000',
      white: '#fff',
      transparent: 'transparent',
      current: 'currentColor',
      inherit: 'inherit',
      red: {
        '200': '#fecaca',
        '300': '#fca5a5',
        '500': '#E2251B',
        DEFAULT: '#E2251B'
      },
      gray: {
        '100': '#F2F3F4',
        '200': '#EFEFEF',
        '300': '#D9DBDD',
        '400': '#B3B7BA',
        '500': '#686E75',
        '700': '#353E47',
        '900': '#1a202c',
        DEFAULT: '#686E75'
      },
      blue: {
        '100': '#EBF7FE',
        '200': '#D7F0FC',
        '400': '#36B3F0',
        '500': '#0869C1',
        '700': '#064F91',
        '900': '#003087',
        '950': '#030E19',
        DEFAULT: '#0869C1'
      },
      cyan: {
        '600': '#009CDE'
      },
      green: {
        '100': '#E5F3F2',
        '200': '#CCE7E5',
        '500': '#00857E',
        DEFAULT: '#00857E'
      },
      purple: {
        '100': '#A14A821A',
        '200': '#ECDBE6',
        '500': '#A14A82',
        DEFAULT: '#A14A82'
      },
      orange: {
        '100': '#F0A1331A',
        '200': '#FCECD6',
        '500': '#F0A133',
        DEFAULT: '#F0A133'
      },
      yellow: {
        '200': '#fde68a',
        '400': '#FFC439',
        DEFAULT: '#FFC439'
      },
      salmon: {
        '200': '#FDE2DF',
        '500': '#F46E60',
        DEFAULT: '#F46E60'
      },
      clay: {
        '200': '#EFEEEE',
        '500': '#B0ACA9',
        DEFAULT: '#B0ACA9'
      }
    },
    // Dont set line height for smaller sizes, as line height varies a lot
    fontSize: ({ theme }) => ({
      xxs: '0.75rem', // 12px
      xs: '0.875rem', // 14px
      sm: '1rem', // 16px
      base: '1.125rem', // 18px
      lg: '1.25rem', // 20px
      xl: ['1.375rem', { lineHeight: theme('lineHeight.tight') }], // 22px
      '2xl': ['1.5rem', { lineHeight: theme('lineHeight.snug') }], // 24px
      '3xl': ['1.625rem', { lineHeight: theme('lineHeight.tight') }], // 26px
      '4xl': ['1.75rem', { lineHeight: theme('lineHeight.snug') }], // 28px
      '5xl': ['2rem', { lineHeight: theme('lineHeight.tight') }], // 32px
      '6xl': ['2.625rem', { lineHeight: theme('lineHeight.snug') }], // 42px
      '7xl': ['3rem', { lineHeight: theme('lineHeight.tighter') }] // 48px
    }),
    extend: {
      // Adjust screens slightly so:
      // - iPad Pro uses tablet layout
      // - large desktop has comfortable gutter
      screens: { lg: '1028px', xl: '1320px' },
      spacing: {
        '18': '4.5rem'
      },
      fontFamily: {
        sans: ['Inter Variable', ...defaultTheme.fontFamily.sans],
        serif: ['Merriweather', ...defaultTheme.fontFamily.serif],
        montserrat: ['Montserrat', ...defaultTheme.fontFamily.serif]
      },
      lineHeight: {
        tighter: '1.15',
        tight: '1.2',
        snug: '1.3',
        normal: '1.45',
        relaxed: '1.55',
        loose: '1.65'
      },
      boxShadow: {
        sm: '0 4px 8px 0 rgba(0, 0, 0, 0.08)',
        md: '0 4px 16px 0 rgba(0, 0, 0, 0.12)',
        lg: '0 4px 24px 0 rgba(0, 0, 0, 0.12)',
        xl: '0 8px 48px 0 rgba(0, 0, 0, 0.12)',
        '-lg': '0 -4px 24px 0 rgba(0, 0, 0, 0.12)',
        '-xl': '0 -8px 48px 0 rgba(0, 0, 0, 0.12)',
        DEFAULT: '0 4px 24px 0 rgba(0, 0, 0, 0.12)'
      },
      borderRadius: {
        sm: '4px',
        md: '8px',
        lg: '16px',
        DEFAULT: '8px'
      },
      borderColor: ({ theme }) => ({
        DEFAULT: theme('colors.gray.300')
      }),
      typography: (theme: PluginAPI['theme']) => tailwindTypographyConfig(theme),
      keyframes: {
        scroll: {
          '0%': { transform: 'translateX(0)' },
          '100%': { transform: 'translateX(-100%)' }
        }
      },
      animation: {
        scroll: 'scroll 10s linear infinite'
      }
    }
  },
  safelist: [
    {
      pattern: /bg-(blue|orange|purple)-100/,
      variants: []
    },
    {
      pattern: /bg-(blue|orange|purple)-200/,
      variants: []
    },
    {
      pattern: /text-(blue|orange|purple)-500/,
      variants: []
    }
  ],
  plugins: [tailwindTypographyPlugin]
} satisfies Config;
